import React, { Fragment, useEffect, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import clsx from "clsx";
import { CalendarCheck, CalendarX, CaretDown, CheckCircle, Clock, EnvelopeSimple, LinkBreak, ShieldCheck } from "phosphor-react";
import Button from "../Button";
import useCurrentUser from "../../auth/useCurrentUser";
import { hasClaim } from "../../auth/hasClaim";
import { UserRole } from "../../models/app/userMe";
import srcConfig from "../../configuration";
import { useDisconnectCalendar, useFetchNylasAuthUrl } from "../../api/nylas";
import { errorToast, successToast } from "../../toast";
import NylasDisconnectDialog from "./NylasDisconnectDialog";

export type NylasConnectIndicatorProps = {
    className?: string
    /**
     * "menu" (default): full pill with dropdown (details + disconnect) and connect/reconnect button.
     * "status": subtle, non-interactive connected/not-connected indicator (e.g. for the side nav).
     */
    variant?: "menu" | "status"
};

const providerDisplayName = (provider?: string | null): string => {
    switch (provider?.toLowerCase()) {
    case "google": return "Google Calendar";
    case "microsoft": return "Outlook Calendar";
    case "icloud": return "iCloud Calendar";
    default: return provider ? `${provider[0].toUpperCase()}${provider.slice(1)} Calendar` : "Calendar";
    }
};

const formatSyncedAgo = (isoDate: string): string => {
    const seconds = Math.max(0, Math.floor((Date.now() - new Date(isoDate).getTime()) / 1000));
    if (seconds < 60) return "Synced just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `Synced ${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Synced ${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `Synced ${days}d ago`;
};

const NylasConnectIndicator: React.FC<NylasConnectIndicatorProps> = ({ className, variant = "menu" }) => {
    const {
        calendarReady,
        needsCalendarReconnect,
        isCurrentUserLoading,
        calendarProvider,
        calendarEmail,
        lastCalendarHealthCheckedAt,
        refetchCurrentUser,
        claims,
        idpUserId,
    } = useCurrentUser();
    const isTalent = hasClaim(UserRole.AC, claims);

    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [shouldFetchAuthUrl, setShouldFetchAuthUrl] = useState(false);
    const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
    const nylasRedirectUri = encodeURIComponent(window.location.origin + srcConfig.nylas.nylasAuthCallbackUri || "");
    const { authUrl, error } = useFetchNylasAuthUrl(nylasRedirectUri, shouldFetchAuthUrl);
    const [disconnectCalendar, isDisconnecting] = useDisconnectCalendar();

    const onConnectClicked = () => {
        if (!idpUserId) {
            errorToast("User ID not found.");
            return;
        }
        setIsAuthenticating(true);
        setShouldFetchAuthUrl(true);
    };

    const onDisconnectConfirmed = async () => {
        const response = await disconnectCalendar();
        if (response.success) {
            setIsDisconnectDialogOpen(false);
            successToast("Calendar disconnected.");
            await refetchCurrentUser();
        } else {
            errorToast("Failed to disconnect calendar. Please try again.");
        }
    };

    useEffect(() => {
        if (!shouldFetchAuthUrl) return;
        if (authUrl) {
            setIsAuthenticating(false);
            setShouldFetchAuthUrl(false);
            window.location.href = authUrl;
        } else if (error) {
            setIsAuthenticating(false);
            setShouldFetchAuthUrl(false);
            errorToast("Failed to fetch authentication URL. Please try again.");
        }
    }, [authUrl, error, shouldFetchAuthUrl]);

    if (!isTalent) return null;

    // Avoid flashing "Connect calendar" before the first /me response tells us the real state.
    if (isCurrentUserLoading) return null;

    if (variant === "status") {
        const statusLabel = calendarReady ? "Calendar connected" : "Calendar not connected";

        return (
            <div className={className} title={statusLabel}>
                <div className="flex items-center gap-1.5 text-sm text-gray-500">
                    {calendarReady
                        ? <CheckCircle size={16} className="text-green-600 shrink-0" />
                        : <CalendarX size={16} className="text-gray-400 shrink-0" />}
                    <span>{statusLabel}</span>
                </div>
            </div>
        );
    }

    if (calendarReady) {
        const providerName = providerDisplayName(calendarProvider);

        return (
            <div className={className}>
                <Menu as="div" className="relative">
                    <Menu.Button
                        className="flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary"
                        title="Calendar connected"
                    >
                        <CalendarCheck size={20} className="text-green-600" />
                        <span className="hidden sm:inline font-bold">Calendar connected</span>
                        <CaretDown size={14} aria-hidden="true" />
                    </Menu.Button>
                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                    >
                        <Menu.Items className="origin-top-right absolute right-0 mt-2 w-72 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                            <div className="px-4 py-3">
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 shrink-0 flex items-center justify-center rounded-lg bg-blue-500 text-white font-bold text-lg">
                                        {providerName[0]}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-semibold truncate">{providerName}</p>
                                        {calendarEmail && (
                                            <p className="flex items-center gap-1 text-sm text-gray-500 truncate">
                                                <EnvelopeSimple size={14} className="shrink-0" />
                                                <span className="truncate">{calendarEmail}</span>
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                                    {lastCalendarHealthCheckedAt && (
                                        <span className="flex items-center gap-1">
                                            <Clock size={14} />
                                            {formatSyncedAgo(lastCalendarHealthCheckedAt)}
                                        </span>
                                    )}
                                    <span className="flex items-center gap-1">
                                        <ShieldCheck size={14} />
                                        Read &amp; write
                                    </span>
                                </div>
                            </div>
                            <div className="border-t border-gray-100" />
                            <Menu.Item>
                                {({ active }) => (
                                    <button
                                        type="button"
                                        onClick={() => setIsDisconnectDialogOpen(true)}
                                        className={clsx(
                                            "w-full flex items-center gap-2 px-4 py-3 text-left text-error rounded-b-lg",
                                            active && "bg-gray-50"
                                        )}
                                    >
                                        <LinkBreak size={16} />
                                        Disconnect calendar
                                    </button>
                                )}
                            </Menu.Item>
                        </Menu.Items>
                    </Transition>
                </Menu>
                <NylasDisconnectDialog
                    isOpen={isDisconnectDialogOpen}
                    onClose={() => setIsDisconnectDialogOpen(false)}
                    onDisconnect={onDisconnectConfirmed}
                    isDisconnecting={isDisconnecting}
                    accountEmail={calendarEmail}
                />
            </div>
        );
    }

    return (
        <div className={className}>
            <Button
                variant="secondary"
                className="px-3 py-1 rounded-full flex items-center gap-3"
                onClick={onConnectClicked}
                loading={isAuthenticating}
                disabled={isAuthenticating}
            >
                <CalendarX size={18} className="text-error mr-1" />
                <span>{needsCalendarReconnect ? "Reconnect calendar" : "Connect calendar"}</span>
            </Button>
        </div>
    );
};

export default NylasConnectIndicator;
